import { useState, useEffect } from "react";
import { configs } from "../configs";

interface Product {
  id: string;
  title: string;
  tags: string[];
  onlineStoreUrl: string;
  availableForSale: boolean;
}

// GraphQL query
const PRODUCTS_QUERY = `
  query($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Product {
        tags
        onlineStoreUrl
        availableForSale
      }
    }
  }
`;

const getProductsById = async (
  productIds: string[],
  storefrontAccessToken: string,
  storeDomain: string
): Promise<Product[]> => {
  if (!productIds.length) {
    return [];
  }

  const ids = productIds.map(id => `gid://shopify/Product/${id.split('/').pop()}`);
  const endpoint = `https://${storeDomain}/api/2024-07/graphql.json`;

  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": storefrontAccessToken,
    },
    body: JSON.stringify({
      query: PRODUCTS_QUERY,
      variables: { ids }
    }),
  });

  const data = await response.json();
  if (response.ok) {
    return data.data.nodes;
  } else {
    throw new Error(data.errors ? data.errors[0].message : "Error fetching product data");
  }
};


export const useShopifyProducts = (productIds: string[]) => {
  const [products, setProducts] = useState<Product[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const { storefrontAccessToken, storeDomain } = configs;

  useEffect(() => {
    if (productIds.length === 0) {
      setProducts([]);
      setLoading(false);
      return;
    }

    const fetchProducts = async () => {
      try {
        const productsData = await getProductsById(productIds, storefrontAccessToken, storeDomain);
        setProducts(productsData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [productIds]);

  return { products, loading, error };
};
